import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Form, Input, Modal, Select, Space, Spin, Table, Tag, TimePicker, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { ProjectLink } from "../../components/ProjectLink";
import { SiteLabel } from "../../components/SiteLabel";
import { useApi } from "../../helpers/useApi";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import type { AttendanceLine, AttendanceSheet, Paginated, Staff } from "../../api-models";

const LINE_STATES = [
  { value: "PRESENT", label: "Present" },
  { value: "ABSENT", label: "Absent" },
  { value: "LEAVE", label: "Leave" },
  { value: "NOT_SCHEDULED", label: "Not scheduled" },
];

// A sheet's lines are only editable in these statuses (mirrors sheetEditable, attendance.go).
const EDITABLE_STATUSES = new Set(["DRAFT", "RETURNED_FOR_CORRECTION", "REOPENED"]);

const TIME_FORMAT = "HH:mm";
// Prefilled when a line is switched to Present, so a full sheet can be marked off without
// retyping the standard shift on every row.
const DEFAULT_CLOCK_IN: [number, number] = [7, 30];
const DEFAULT_CLOCK_OUT: [number, number] = [19, 0];

interface AddStaffValues {
  staffId?: number;
  exceptionReason?: string;
}

// What the row's inputs hold between keystrokes/picks. Times are kept as picked (the
// TimePicker only ever shows the time part); the sheet's date and any overnight rollover
// are applied at save time by clockTimes().
interface LineDraft {
  state: string;
  clockIn: Dayjs | null;
  clockOut: Dayjs | null;
  exceptionReason: string;
}

function draftFromLine(line: AttendanceLine): LineDraft {
  return {
    state: line.state,
    clockIn: line.clockInAt ? dayjs(line.clockInAt) : null,
    clockOut: line.clockOutAt ? dayjs(line.clockOutAt) : null,
    exceptionReason: line.exceptionReason ?? "",
  };
}

// Pins the picked times to the sheet's attendance date. A clock-out that isn't after the
// clock-in is read as a night shift ending the next morning - writeAttendanceLine
// (attendance.go) refuses anything else, and the resulting hours are flagged by the
// reasonableness limit if the times were simply a typo.
function clockTimes(day: Dayjs, draft: LineDraft): { clockInAt?: string; clockOutAt?: string } {
  const onDay = (t: Dayjs) => day.hour(t.hour()).minute(t.minute()).second(0).millisecond(0);
  const clockIn = draft.clockIn ? onDay(draft.clockIn) : null;
  let clockOut = draft.clockOut ? onDay(draft.clockOut) : null;
  if (clockIn && clockOut && !clockOut.isAfter(clockIn)) clockOut = clockOut.add(1, "day");
  return { clockInAt: clockIn?.toISOString(), clockOutAt: clockOut?.toISOString() };
}

export default function AttendanceSheetDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const { request, loading } = useApi();

  const [sheet, setSheet] = useState<AttendanceSheet | null>(null);
  const [staffOptions, setStaffOptions] = useState<Staff[]>([]);
  const [drafts, setDrafts] = useState<Record<number, LineDraft>>({});
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [form] = Form.useForm<AddStaffValues>();

  const canWrite = hasPermission(user, "Attendance.Write", sheet?.siteId);
  const canLock = hasPermission(user, "Attendance.Lock");
  const editable = !!sheet && EDITABLE_STATUSES.has(sheet.status);
  const day = sheet ? dayjs(sheet.attendanceDate) : dayjs();

  async function load() {
    const res = await request<AttendanceSheet>({ url: `/attendance-sheets/${id}` });
    setSheet(res);
    setDrafts(Object.fromEntries((res.lines ?? []).map((l) => [l.id, draftFromLine(l)])));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!sheet) return;
    const canBrowseAllStaff = hasPermission(user, "Staff.Read");
    request<Paginated<Staff>>({ url: "/staff", params: canBrowseAllStaff ? { pageSize: 500 } : { pageSize: 500, siteId: sheet.siteId } }).then(
      (res) => setStaffOptions(res.data),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet?.siteId]);

  async function act(action: string) {
    await request({ method: "POST", url: `/attendance-sheets/${id}/${action}` });
    message.success("Done.");
    load();
  }

  async function reopen() {
    if (!reopenReason.trim()) {
      message.error("A reason is required to reopen a locked attendance sheet.");
      return;
    }
    await request({ method: "POST", url: `/attendance-sheets/${id}/reopen`, data: { reason: reopenReason } });
    setReopenOpen(false);
    setReopenReason("");
    message.success("Attendance sheet reopened.");
    load();
  }

  // Each cell edit is saved on its own - attendance is filled in row by row, and the server
  // is what turns clock times into gross/regular/OT hours and exception flags, so the row is
  // refreshed from its response rather than guessed at locally.
  async function saveLine(line: AttendanceLine, patch: Partial<LineDraft>) {
    const draft = { ...(drafts[line.id] ?? draftFromLine(line)), ...patch };
    setDrafts((d) => ({ ...d, [line.id]: draft }));
    setSavingIds((s) => new Set(s).add(line.id));
    try {
      const times = draft.state === "PRESENT" ? clockTimes(day, draft) : {};
      const updated = await request<AttendanceLine>({
        method: "PUT",
        url: `/attendance-sheets/${id}/lines/${line.id}`,
        data: { state: draft.state, ...times, exceptionReason: draft.exceptionReason },
      });
      setSheet((s) => (s ? { ...s, lines: (s.lines ?? []).map((l) => (l.id === line.id ? { ...l, ...updated, staff: l.staff } : l)) } : s));
      setDrafts((d) => ({ ...d, [line.id]: draftFromLine(updated) }));
    } catch {
      // useApi has already surfaced the message; the row keeps what was entered so it can be
      // corrected in place instead of being silently reverted.
    } finally {
      setSavingIds((s) => {
        const next = new Set(s);
        next.delete(line.id);
        return next;
      });
    }
  }

  function changeState(line: AttendanceLine, state: string) {
    const draft = drafts[line.id] ?? draftFromLine(line);
    const patch: Partial<LineDraft> = { state };
    if (state === "PRESENT") {
      patch.clockIn = draft.clockIn ?? day.hour(DEFAULT_CLOCK_IN[0]).minute(DEFAULT_CLOCK_IN[1]);
      patch.clockOut = draft.clockOut ?? day.hour(DEFAULT_CLOCK_OUT[0]).minute(DEFAULT_CLOCK_OUT[1]);
    }
    saveLine(line, patch);
  }

  function openAddStaff() {
    form.resetFields();
    setAddOpen(true);
  }

  async function addStaff() {
    const values = await form.validateFields();
    await request({
      method: "POST",
      url: `/attendance-sheets/${id}/lines`,
      data: { staffId: values.staffId, state: "NOT_SCHEDULED", exceptionReason: values.exceptionReason ?? "" },
    });
    setAddOpen(false);
    load();
  }

  if (!sheet) return null;

  const assignedStaffIds = new Set((sheet.lines ?? []).map((l) => l.staffId));
  const addableStaff = staffOptions.filter((s) => !assignedStaffIds.has(s.id));
  const canEditLines = editable && canWrite;
  // Rows the server has flagged and nobody has explained yet - the only thing standing
  // between a part-filled draft and submission (handleSubmitAttendanceSheet, attendance.go).
  const unresolved = (sheet.lines ?? []).filter((l) => l.hasException && !(drafts[l.id]?.exceptionReason ?? l.exceptionReason));

  return (
    <div>
      <PageHeader
        title={sheet.docNo}
        subtitle={
          <>
            <SiteLabel site={sheet.site} project={sheet.project} /> - {dayjs(sheet.attendanceDate).format(DT_FORMATS.date)} -{" "}
            {sheet.classification.replaceAll("_", " ")}
          </>
        }
        extra={
          <Space>
            <span data-tour="doc-status">
              <DocumentStatusTag status={sheet.status} />
            </span>
            {editable && canWrite && (
              <Button onClick={() => act("submit")} loading={loading} data-tour="attendance-submit">
                Submit for approval
              </Button>
            )}
            {(sheet.status === "DRAFT" || sheet.status === "SUBMITTED" || sheet.status === "RETURNED_FOR_CORRECTION") && (canWrite || canLock) && (
              <Button danger onClick={() => act("cancel")} loading={loading}>
                Cancel
              </Button>
            )}
            {(sheet.status === "HR_APPROVED" || sheet.status === "REOPENED") && canLock && (
              <Button type="primary" onClick={() => act("lock")} loading={loading} data-tour="attendance-lock">
                Lock
              </Button>
            )}
            {sheet.status === "LOCKED" && canLock && (
              <Button onClick={() => setReopenOpen(true)} loading={loading}>
                Reopen
              </Button>
            )}
          </Space>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={3} size="small">
          <Descriptions.Item label="Project"><ProjectLink project={sheet.project} /></Descriptions.Item>
          <Descriptions.Item label="Supervisor">{sheet.supervisor?.name}</Descriptions.Item>
          <Descriptions.Item label="Lock version">{sheet.lockVersion}</Descriptions.Item>
          {sheet.reopenReason && <Descriptions.Item label="Reopen reason">{sheet.reopenReason}</Descriptions.Item>}
        </Descriptions>
      </Card>
      <Card
        size="small"
        title="Attendance lines"
        data-tour="attendance-lines"
        extra={
          canEditLines && (
            <Button size="small" onClick={openAddStaff} disabled={addableStaff.length === 0}>
              Add staff
            </Button>
          )
        }
      >
        {canEditLines && (
          <Alert
            type={unresolved.length > 0 ? "warning" : "info"}
            showIcon
            style={{ marginBottom: 12 }}
            message={
              unresolved.length > 0
                ? `Saved as a draft. ${unresolved.length} row${unresolved.length === 1 ? "" : "s"} still need an exception reason before this sheet can be submitted.`
                : "Saved as a draft. Every change is kept as you go, so the sheet can be left part-filled and finished later."
            }
          />
        )}
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: "max-content" }}
          dataSource={sheet.lines ?? []}
          columns={[
            { title: "Staff", render: (_, r: AttendanceLine) => `${r.staff?.staffCode ?? r.staffId} - ${r.staff?.name ?? ""}` },
            {
              title: "State",
              dataIndex: "state",
              render: (v, r: AttendanceLine) =>
                canEditLines ? (
                  <Select
                    size="small"
                    style={{ width: 140 }}
                    value={drafts[r.id]?.state ?? v}
                    options={LINE_STATES}
                    onChange={(next) => changeState(r, next)}
                  />
                ) : (
                  <DocumentStatusTag status={v} />
                ),
            },
            ...(["clockIn", "clockOut"] as const).map((field) => ({
              title: field === "clockIn" ? "Clock in" : "Clock out",
              key: field,
              render: (_: unknown, r: AttendanceLine) => {
                const draft = drafts[r.id] ?? draftFromLine(r);
                const stored = field === "clockIn" ? r.clockInAt : r.clockOutAt;
                if (!canEditLines) return stored ? dayjs(stored).format(DT_FORMATS.dateTime) : "-";
                return (
                  <TimePicker
                    size="small"
                    style={{ width: 96 }}
                    format={TIME_FORMAT}
                    needConfirm={false}
                    disabled={draft.state !== "PRESENT"}
                    value={draft[field]}
                    onChange={(t) => saveLine(r, { [field]: t })}
                  />
                );
              },
            })),
            { title: "Gross", dataIndex: "grossHours" },
            { title: "Regular", dataIndex: "regularHours" },
            { title: "OT", dataIndex: "overtimeHours" },
            {
              title: "Exception",
              key: "exception",
              render: (_, r: AttendanceLine) => {
                const draft = drafts[r.id] ?? draftFromLine(r);
                if (!canEditLines) {
                  return r.hasException ? <Tag color={r.exceptionReason ? "orange" : "red"}>{r.exceptionReason || "Unresolved"}</Tag> : null;
                }
                return (
                  <Input
                    size="small"
                    style={{ width: 220 }}
                    status={r.hasException && !r.exceptionReason ? "error" : undefined}
                    placeholder={r.hasException ? "Reason required" : ""}
                    value={draft.exceptionReason}
                    onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: { ...draft, exceptionReason: e.target.value } }))}
                    onBlur={() => draft.exceptionReason !== (r.exceptionReason ?? "") && saveLine(r, {})}
                    onPressEnter={(e) => e.currentTarget.blur()}
                  />
                );
              },
            },
            { title: "Source", dataIndex: "source" },
            ...(canEditLines
              ? [
                  {
                    title: "",
                    key: "saving",
                    width: 32,
                    render: (_: unknown, r: AttendanceLine) => (savingIds.has(r.id) ? <Spin size="small" /> : null),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      <ApprovalTimeline docType="ATTENDANCE_SHEET" docId={sheet.id} refreshKey={sheet.status} onActed={load} />

      {/* Only picks who goes on the sheet - their state and clock times are then filled in
          on the row itself. */}
      <Modal title="Add staff" open={addOpen} onCancel={() => setAddOpen(false)} onOk={addStaff} confirmLoading={loading} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="staffId" label="Staff" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={addableStaff.map((s) => ({ value: s.id, label: `${s.staffCode} - ${s.name}` }))} />
          </Form.Item>
          {/* Optional here: a staff member with no assignment for this site/date is added
              anyway and flagged, and the reason can be filled in on the row later - it is
              only needed before the sheet is submitted. */}
          <Form.Item name="exceptionReason" label="Exception reason (needed before submission if they have no assignment for this site/date)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Reopen locked attendance sheet" open={reopenOpen} onCancel={() => setReopenOpen(false)} onOk={reopen} confirmLoading={loading}>
        <Input.TextArea rows={3} placeholder="Reason (required)" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} />
      </Modal>
    </div>
  );
}
